'use client';

import { createContext, useContext, ReactNode, useRef, useCallback, useEffect, useState } from 'react';

// Services
import { createWebSocketService, type WebSocketService, type WebSocketConfig, type WebSocketMessage } from '@/services/websocket';

// Utils
import { showErrorToast } from '@/utils/errorHandlers';
import { getWebSocketUrl } from '@/utils/endpoint';

// Constants
import { WEBSOCKET_MESSAGE, BASE_URL_ENDPOINT, WEBSOCKET_CONFIG } from '@/constants';
import ENV from '@shared/configs';

// Providers
import { useFirebase } from '@/providers/firebase';
interface WebSocketConnection {
  service: WebSocketService | null;
  unsubscribe: (() => void) | null;
  listeners: Set<(message?: WebSocketMessage) => void>;
}
interface WebSocketContextType {
  connect: (url?: string) => Promise<void>;
  disconnect: (url?: string) => void;
  disconnectAll: () => void;
  isConnected: (url?: string) => boolean;
  send: (message: Record<string, unknown>, url?: string) => void;
  onMessage: <T>(callback: (message?: WebSocketMessage<T>) => void, url?: string) => () => void;
  connectedUrls: string[];
}
export const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);
interface WebSocketProviderProps {
  children: ReactNode;
  defaultUrl?: string;
  autoConnect?: boolean;
}
export const WebSocketProvider = ({
  children,
  defaultUrl = ENV.API_ENDPOINT,
  autoConnect = false
}: WebSocketProviderProps) => {
  const connectionsRef = useRef<Map<string, WebSocketConnection>>(new Map());
  const {
    getToken,
    isLoading
  } = useFirebase();
  const [connectedUrls, setConnectedUrls] = useState<string[]>([]);

  // Helper to update connected URLs state
  const updateConnectedUrls = useCallback(() => {
    const urls = Array.from(connectionsRef.current.keys()).filter(url => {
      const connection = connectionsRef.current.get(url);
      return connection?.service?.isSocketConnected() ?? false;
    });
    setConnectedUrls(urls);
  }, []);

  // Format WebSocket URL with token
  const formatWebSocketUrl = useCallback(async (url: string) => {
    const token = await getToken();
    if (!token) return null;

    // Convert HTTP(S) to WS(S)
    const wsUrl = getWebSocketUrl(url);
    return `${wsUrl}${BASE_URL_ENDPOINT}/ws?token=${token}`;
  }, [getToken]);
  const createConnection = useCallback((url: string, listeners: Set<(message?: WebSocketMessage) => void>) => {
    const {
      MAX_RECONNECT_ATTEMPTS,
      RECONNECT_INTERVAL
    } = WEBSOCKET_CONFIG;
    const config: WebSocketConfig = {
      reconnectInterval: RECONNECT_INTERVAL,
      maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
      resolveUrl: () => formatWebSocketUrl(url),
      onOpen: updateConnectedUrls,
      onClose: updateConnectedUrls,
      onError: updateConnectedUrls
    };
    const service = createWebSocketService(config);
    const unsubscribe = service.subscribe(message => {
      listeners.forEach(listener => {
        listener(message);
      });
    });
    return {
      service,
      unsubscribe,
      listeners
    };
  }, [formatWebSocketUrl, updateConnectedUrls]);

  // Disconnect from a specific URL
  const disconnect = useCallback((url: string = defaultUrl) => {
    const connection = connectionsRef.current.get(url);
    if (connection) {
      if (connection.unsubscribe) {
        connection.unsubscribe();
      }
      connection.service?.disconnect();
      connectionsRef.current.delete(url);
      updateConnectedUrls();
    }
  }, [defaultUrl, updateConnectedUrls]);

  // Check if a specific URL is connected
  const isConnected = useCallback((url: string = defaultUrl): boolean => {
    const connection = connectionsRef.current.get(url);
    return connection?.service?.isSocketConnected() ?? false;
  }, [defaultUrl]);

  // Connect to a specific URL
  const connectToUrl = useCallback(async (url: string = defaultUrl, showError = true) => {
    try {
      const existingConnection = connectionsRef.current.get(url);
      if (existingConnection?.service?.isSocketConnected()) {
        updateConnectedUrls();
        return;
      }
      if (existingConnection?.service) {
        existingConnection.unsubscribe?.();
        existingConnection.service.disconnect();
        const refreshedConnection = createConnection(url, existingConnection.listeners);
        connectionsRef.current.set(url, refreshedConnection);
        await refreshedConnection.service?.connect();
        updateConnectedUrls();
        return;
      }
      const listeners = existingConnection?.listeners ?? new Set<(message?: WebSocketMessage) => void>();
      const connection = createConnection(url, listeners);
      connectionsRef.current.set(url, connection);
      await connection.service?.connect();
      updateConnectedUrls();
    } catch (error) {
      updateConnectedUrls();
      if (showError) {
        showErrorToast(WEBSOCKET_MESSAGE.CONNECT_FAILED);
      }
      throw error;
    }
  }, [createConnection, defaultUrl, updateConnectedUrls]);

  // Connect to a specific URL
  const connect = useCallback((url: string = defaultUrl) => {
    return connectToUrl(url, true);
  }, [connectToUrl, defaultUrl]);

  // Disconnect all connections
  const disconnectAll = useCallback(() => {
    connectionsRef.current.forEach(connection => {
      if (connection.unsubscribe) {
        connection.unsubscribe();
      }
      connection.service?.disconnect();
    });
    connectionsRef.current.clear();
    updateConnectedUrls();
  }, [updateConnectedUrls]);

  // Send message to a specific URL
  const send = useCallback((message: Record<string, unknown>, url: string = defaultUrl) => {
    const connection = connectionsRef.current.get(url);
    if (connection?.service?.isSocketConnected()) {
      connection.service.send(message);
    }
  }, [defaultUrl]);

  // Subscribe to messages from a specific URL
  const onMessage = useCallback(<T,>(callback: (message?: WebSocketMessage<T>) => void, url: string = defaultUrl) => {
    const connection = connectionsRef.current.get(url);

    // If connection doesn't exist, create a placeholder for listeners
    if (!connection) {
      const listeners = new Set<(message?: WebSocketMessage) => void>();
      listeners.add(callback as (message?: WebSocketMessage) => void);

      // Note: The actual connection will be established when connect() is called
      connectionsRef.current.set(url, {
        service: null,
        unsubscribe: null,
        listeners
      });
      return () => {
        const conn = connectionsRef.current.get(url);
        conn?.listeners.delete(callback as (message?: WebSocketMessage) => void);
      };
    }
    connection.listeners.add(callback as (message?: WebSocketMessage) => void);
    return () => {
      connection?.listeners.delete(callback as (message?: WebSocketMessage) => void);
    };
  }, [defaultUrl]);
  const reconnectTrackedConnections = useCallback(() => {
    if (isLoading) {
      return;
    }
    const trackedUrls = new Set<string>(connectionsRef.current.keys());
    if (autoConnect && defaultUrl) {
      trackedUrls.add(defaultUrl);
    }
    trackedUrls.forEach(url => {
      const connection = connectionsRef.current.get(url);
      if (connection?.service?.isSocketConnected()) {
        return;
      }
      connectToUrl(url, false).catch(() => {});
    });
  }, [autoConnect, connectToUrl, defaultUrl, isLoading]);

  // Reconnect tracked URLs once auth state is ready.
  useEffect(() => {
    if (isLoading) {
      return;
    }
    reconnectTrackedConnections();
  }, [isLoading, reconnectTrackedConnections]);
  useEffect(() => () => {
    disconnectAll();
  }, [disconnectAll]);
  useEffect(() => {
    const handleForegroundReconnect = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      reconnectTrackedConnections();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        reconnectTrackedConnections();
      }
    };
    window.addEventListener('focus', handleForegroundReconnect);
    window.addEventListener('online', handleForegroundReconnect);
    window.addEventListener('pageshow', handleForegroundReconnect);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleForegroundReconnect);
      window.removeEventListener('online', handleForegroundReconnect);
      window.removeEventListener('pageshow', handleForegroundReconnect);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [reconnectTrackedConnections]);
  const contextValue: WebSocketContextType = {
    connect,
    disconnect,
    disconnectAll,
    isConnected,
    send,
    onMessage,
    connectedUrls
  };
  return <WebSocketContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="WebSocketProvider" data-sentry-source-file="websocket.tsx">
      {children}
    </WebSocketContext.Provider>;
};

/**
 * Hook to use WebSocket context
 */
export const useWebSocketContext = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};